import React, { useState, useRef } from 'react';
import axios from 'axios';
import { cn } from '../../lib/utils';
import { toast } from 'sonner';
import { ImagePlus, Loader2, X, Camera } from 'lucide-react';

const API_URL = '/api';

/**
 * IllustrationUpload - Elegant image upload component for NPC/lore illustrations.
 *
 * Props:
 * - imageUrl: current image URL (or empty)
 * - onImageChange: callback(newUrl) called after upload or remove
 * - entityType: 'npc' | 'lore'
 * - entityId: id of the entity (required for upload)
 * - campaignId: campaign id
 * - language: 'fr' | 'en'
 * - editable: whether the user can change the image (default: true)
 * - size: 'sm' | 'md' | 'lg' | 'banner' (default: 'md')
 * - shape: 'circle' | 'rounded' | 'square' (default: 'rounded')
 * - fallbackIcon: React element to show when no image
 * - fallbackText: text for fallback (e.g. initials)
 * - className: additional classes
 */
export function IllustrationUpload({
  imageUrl,
  onImageChange,
  entityType,
  entityId,
  campaignId,
  language = 'fr',
  editable = true,
  size = 'md',
  shape = 'rounded',
  fallbackIcon,
  fallbackText,
  className
}) {
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef(null);

  const sizeClasses = {
    sm: 'h-12 w-12',
    md: 'h-24 w-24',
    lg: 'h-36 w-36',
    banner: 'h-40 w-full'
  };

  const shapeClasses = {
    circle: 'rounded-full',
    rounded: 'rounded-xl',
    square: 'rounded-md'
  };

  const handleFileSelect = async (file) => {
    if (!file) return;

    const allowed = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    if (!allowed.includes(file.type)) {
      toast.error(language === 'fr'
        ? 'Format non supporté. Utilisez JPG, PNG, WebP ou GIF.'
        : 'Unsupported format. Use JPG, PNG, WebP or GIF.');
      return;
    }

    if (file.size > 8 * 1024 * 1024) {
      toast.error(language === 'fr' ? 'Fichier trop lourd (max 8 Mo)' : 'File too large (max 8 MB)');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(
        `${API_URL}/campaigns/${campaignId}/upload-illustration?entity_type=${entityType}&entity_id=${entityId}`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );

      onImageChange(response.data.image_url);
      toast.success(language === 'fr' ? 'Illustration importée' : 'Illustration uploaded');
    } catch (error) {
      toast.error(error.response?.data?.detail || (language === 'fr' ? 'Erreur d\'import' : 'Upload error'));
    } finally {
      setUploading(false);
    }
  };

  const handleRemove = async () => {
    onImageChange('');
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    if (!editable || uploading) return;
    const file = e.dataTransfer.files[0];
    if (file) handleFileSelect(file);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    if (editable && !uploading) setDragOver(true);
  };

  const isBanner = size === 'banner';
  const hasImage = imageUrl && imageUrl.length > 0;

  return (
    <div
      className={cn(
        "relative group overflow-hidden transition-all duration-200",
        sizeClasses[size],
        shapeClasses[shape],
        dragOver && "ring-2 ring-primary ring-offset-2 ring-offset-background",
        className
      )}
      onDrop={handleDrop}
      onDragOver={handleDragOver}
      onDragLeave={() => setDragOver(false)}
    >
      {hasImage ? (
        <>
          <img
            src={imageUrl}
            alt=""
            className={cn(
              "object-cover w-full h-full",
              shapeClasses[shape]
            )}
          />
          {/* Hover overlay */}
          {editable && (
            <div className={cn(
              "absolute inset-0 bg-black/0 group-hover:bg-black/50 transition-all duration-200 flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100",
              shapeClasses[shape]
            )}>
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="p-2 rounded-full bg-white/20 hover:bg-white/30 text-white transition-colors backdrop-blur-sm"
                title={language === 'fr' ? 'Changer l\'image' : 'Change image'}
              >
                <Camera className="h-4 w-4" />
              </button>
              <button
                type="button"
                onClick={handleRemove}
                className="p-2 rounded-full bg-white/20 hover:bg-red-500/60 text-white transition-colors backdrop-blur-sm"
                title={language === 'fr' ? 'Supprimer' : 'Remove'}
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          )}
        </>
      ) : (
        <div
          className={cn(
            "w-full h-full flex flex-col items-center justify-center border-2 border-dashed transition-all duration-200",
            shapeClasses[shape],
            editable
              ? "border-border/60 hover:border-primary/50 cursor-pointer bg-secondary/30 hover:bg-secondary/50"
              : "border-border/30 bg-secondary/20",
            dragOver && "border-primary bg-primary/5"
          )}
          onClick={() => editable && fileInputRef.current?.click()}
        >
          {uploading ? (
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          ) : fallbackText ? (
            <span className={cn(
              "font-cinzel font-bold text-muted-foreground",
              size === 'sm' ? 'text-xs' : size === 'md' ? 'text-lg' : 'text-2xl'
            )}>
              {fallbackText}
            </span>
          ) : fallbackIcon ? (
            fallbackIcon
          ) : (
            <>
              <ImagePlus className={cn(
                "text-muted-foreground/50 mb-1",
                isBanner ? "h-6 w-6" : size === 'sm' ? "h-4 w-4" : "h-5 w-5"
              )} />
              {(size === 'lg' || isBanner) && editable && (
                <span className="text-[10px] text-muted-foreground/50 text-center px-2">
                  {language === 'fr' ? 'Ajouter une illustration' : 'Add illustration'}
                </span>
              )}
            </>
          )}
        </div>
      )}

      {/* Loading overlay */}
      {uploading && hasImage && (
        <div className={cn(
          "absolute inset-0 bg-black/50 flex items-center justify-center",
          shapeClasses[shape]
        )}>
          <Loader2 className="h-6 w-6 animate-spin text-white" />
        </div>
      )}

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/gif"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) handleFileSelect(file);
          e.target.value = '';
        }}
        className="hidden"
      />
    </div>
  );
}
